import { useEffect, useState } from 'react';
import { CollapsibleCard } from './CollapsibleCard';

const INPUT_STYLE = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
} as const;

/**
 * Phone Morning note + sleep (R8.11, today-phone-parity.md §4.3). Reuses
 * `checkin.note_set` verbatim (R1's original note field, R8.5's D6 ruling) —
 * no second event type. Note and sleep stack VERTICALLY, never side by side
 * (§4.3's explicit "the exact shape of v2.3's mood/energy failure" caution).
 * Does NOT include the 7-day mood trend chart — no chart component is
 * imported into this file at all (out of scope, no-dense-computation test).
 */
export function MorningNoteSleepCardPhone({
  note,
  sleepHours,
  onSetNote,
  onSetSleepHours,
  isLast,
}: {
  readonly note: string;
  readonly sleepHours: number | null;
  readonly onSetNote: (next: string) => void;
  readonly onSetSleepHours: (next: number) => void;
  readonly isLast: boolean;
}) {
  const [noteDraft, setNoteDraft] = useState(note);
  const [sleepDraft, setSleepDraft] = useState(sleepHours === null ? '' : String(sleepHours));

  useEffect(() => setNoteDraft(note), [note]);
  useEffect(() => setSleepDraft(sleepHours === null ? '' : String(sleepHours)), [sleepHours]);

  function commitSleep() {
    const parsed = Number(sleepDraft);
    if (sleepDraft.trim() === '' || Number.isNaN(parsed)) return;
    const clamped = Math.min(24, Math.max(0, parsed));
    onSetSleepHours(clamped);
  }

  const summary = note.trim().length > 0 ? note : undefined;

  return (
    <CollapsibleCard title="Morning note" summary={summary} isLast={isLast}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <input
          aria-label="Day-summary note"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={() => onSetNote(noteDraft)}
          placeholder="Steady — coffee, a short walk, nothing dramatic."
          style={INPUT_STYLE}
        />
        <div style={{ height: 'var(--space-3)' }} />
        <div>
          <span
            style={{
              fontSize: 'var(--type-caption-size)',
              lineHeight: 'var(--type-caption-line)',
              letterSpacing: 'var(--type-label-tracking)',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              fontWeight: 600,
            }}
          >
            Sleep
          </span>
          <div style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
            <input
              aria-label="Sleep hours"
              type="number"
              min={0}
              max={24}
              step={0.5}
              value={sleepDraft}
              onChange={(e) => setSleepDraft(e.target.value)}
              onBlur={commitSleep}
              style={{ ...INPUT_STYLE, width: '80px', padding: '0 var(--space-2)' }}
            />
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>hrs</span>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
}
